/**
 * PerformerProfilePage — public page at /performers/{type}s/{slug}.
 * Hero image, bio, Instagram, photo gallery, Upcoming / Past event tabs.
 * The performer (owner) and admins can edit inline.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Loader2, Instagram, Calendar, MapPin, Pencil, Mic2, ImagePlus, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPE_LABEL = { dj: 'DJ', band: 'Band', comedian: 'Comedian', speaker: 'Speaker', artist: 'Artist', musician: 'Musician', performer: 'Performer' };

function EventCard({ e, past }) {
  return (
    <Link to={`/events/${e.city || 'denver'}/${e.slug}`}
      className={`block rounded-xl border border-border overflow-hidden hover:border-indigo-500/50 transition-colors ${past ? 'opacity-75' : ''}`}
      data-testid={`performer-event-${e.event_id}`}>
      {e.image_url && <img src={e.image_url} alt={e.name} className="w-full h-32 object-cover" />}
      <div className="p-3 space-y-1">
        <p className="font-semibold text-sm truncate">{e.name}</p>
        <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Calendar className="h-3 w-3" /> {e.event_date}{e.event_time ? ` · ${e.event_time}` : ''}</p>
        {e.venue_name && <p className="text-xs text-muted-foreground flex items-center gap-1.5 truncate"><MapPin className="h-3 w-3" /> {e.venue_name}</p>}
      </div>
    </Link>
  );
}

export default function PerformerProfilePage() {
  const { ptype, slug } = useParams();
  const { user, token } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('upcoming');
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/performers/${ptype}/${slug}`)
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.detail || 'Performer not found'));
  }, [ptype, slug]);

  if (error) return <div className="min-h-screen flex items-center justify-center p-6"><p className="text-muted-foreground" data-testid="performer-page-error">{error}</p></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-400" /></div>;

  const p = data.profile;
  const canEdit = user && (user.role === 'admin' || user.user_id === p.owner_user_id || user.user_id === p.created_by_user_id);
  const hero = p.hero_image || p.gallery?.[0] || data.upcoming_events?.[0]?.image_url;
  const events = tab === 'upcoming' ? data.upcoming_events : data.past_events;

  const uploadTo = async (files, target) => {
    setUploading(true);
    try {
      const urls = [];
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/upload`, fd, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
        urls.push(`${API}/files/${res.data.path}`);
      }
      if (target === 'hero') setEdit(f => ({ ...f, hero_image: urls[0] }));
      else setEdit(f => ({ ...f, gallery: [...(f.gallery || []), ...urls].slice(0, 24) }));
    } catch (e) { toast.error('Upload failed'); }
    setUploading(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/performer-profiles/${p.profile_id}`, edit, { headers: { Authorization: `Bearer ${token}` } });
      setData(d => ({ ...d, profile: { ...d.profile, ...res.data } }));
      setEdit(null);
      toast.success('Profile updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': p.performer_type === 'band' ? 'MusicGroup' : 'Person',
    name: p.name,
    ...(p.bio ? { description: p.bio } : {}),
    ...(hero ? { image: hero } : {}),
    ...(p.instagram ? { sameAs: [p.instagram] } : {}),
    url: `https://eventism.com${data.url}`,
  };

  return (
    <div className="min-h-screen pb-14" data-testid="performer-profile-page">
      <SEO
        title={`${p.name} — ${TYPE_LABEL[p.performer_type] || 'Performer'} | Eventism`}
        description={p.bio ? p.bio.slice(0, 155) : `Upcoming events and photos from ${p.name} on Eventism.`}
        image={hero}
        structuredData={jsonLd}
      />
      <div className="relative h-64 sm:h-80 w-full overflow-hidden">
        {hero ? <img src={hero} alt={p.name} className="w-full h-full object-cover" data-testid="performer-hero" />
          : <div className="w-full h-full bg-gradient-to-br from-indigo-900 via-fuchsia-900 to-slate-900" />}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 -mt-16 relative space-y-8">
        <div className="space-y-2">
          <Badge className="bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30 uppercase text-[10px] tracking-widest">
            <Mic2 className="h-3 w-3 mr-1" /> {TYPE_LABEL[p.performer_type] || 'Performer'}
          </Badge>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <h1 className="text-4xl sm:text-5xl font-bold" data-testid="performer-name">{p.name}</h1>
            <div className="flex gap-2">
              {p.instagram && (
                <Button asChild variant="outline" size="sm" className="rounded-full" data-testid="performer-instagram-link">
                  <a href={p.instagram} target="_blank" rel="noopener noreferrer"><Instagram className="h-4 w-4 mr-1.5" /> Instagram</a>
                </Button>
              )}
              {canEdit && !edit && (
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEdit({ bio: p.bio, instagram: p.instagram, hero_image: p.hero_image, gallery: p.gallery || [] })} data-testid="performer-edit-btn">
                  <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit page
                </Button>
              )}
            </div>
          </div>
          {!edit && p.bio && <p className="text-muted-foreground max-w-2xl leading-relaxed" data-testid="performer-bio">{p.bio}</p>}
        </div>

        {edit && (
          <div className="rounded-xl border border-fuchsia-500/30 p-4 space-y-3" data-testid="performer-edit-form">
            <Textarea rows={3} value={edit.bio} onChange={e => setEdit({ ...edit, bio: e.target.value })} placeholder="Bio" data-testid="performer-edit-bio" />
            <Input value={edit.instagram || ''} onChange={e => setEdit({ ...edit, instagram: e.target.value })} placeholder="Instagram (@handle or URL)" data-testid="performer-edit-instagram" />
            <div className="flex flex-wrap items-center gap-2">
              <label className="text-xs font-semibold">Hero:</label>
              {edit.hero_image && <img src={edit.hero_image} alt="" className="h-12 w-20 object-cover rounded" />}
              <label className="h-12 w-12 rounded-md border border-dashed border-fuchsia-500/40 flex items-center justify-center cursor-pointer">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4 text-fuchsia-400" />}
                <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.length && uploadTo(e.target.files, 'hero')} data-testid="performer-edit-hero-input" />
              </label>
              <label className="text-xs font-semibold ml-3">Gallery:</label>
              {(edit.gallery || []).map((u, i) => (
                <div key={u + i} className="relative h-12 w-12 rounded overflow-hidden group">
                  <img src={u} alt="" className="h-full w-full object-cover" />
                  <button onClick={() => setEdit(f => ({ ...f, gallery: f.gallery.filter((_, x) => x !== i) }))} className="absolute top-0 right-0 bg-black/70 rounded-bl p-0.5 opacity-0 group-hover:opacity-100"><X className="h-3 w-3 text-white" /></button>
                </div>
              ))}
              <label className="h-12 w-12 rounded-md border border-dashed border-fuchsia-500/40 flex items-center justify-center cursor-pointer">
                <ImagePlus className="h-4 w-4 text-fuchsia-400" />
                <input type="file" accept="image/*" multiple className="hidden" onChange={e => e.target.files?.length && uploadTo(e.target.files, 'gallery')} data-testid="performer-edit-gallery-input" />
              </label>
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="rounded-full font-semibold" disabled={saving} onClick={save} data-testid="performer-edit-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}</Button>
              <Button size="sm" variant="ghost" onClick={() => setEdit(null)}>Cancel</Button>
            </div>
          </div>
        )}

        {(p.gallery || []).length > 0 && !edit && (
          <div data-testid="performer-gallery">
            <h2 className="text-lg font-bold mb-3">Gallery</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {p.gallery.slice(0, 8).map((u, i) => (
                <img key={u + i} src={u} alt={`${p.name} ${i + 1}`} className="w-full aspect-square object-cover rounded-lg" />
              ))}
            </div>
          </div>
        )}

        <div>
          <div className="flex gap-2 mb-4">
            <button onClick={() => setTab('upcoming')} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${tab === 'upcoming' ? 'bg-indigo-500 text-white' : 'bg-muted text-muted-foreground'}`} data-testid="performer-tab-upcoming">
              Upcoming ({data.upcoming_events.length})
            </button>
            <button onClick={() => setTab('past')} className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${tab === 'past' ? 'bg-indigo-500 text-white' : 'bg-muted text-muted-foreground'}`} data-testid="performer-tab-past">
              Past ({data.past_events.length})
            </button>
          </div>
          {events.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6" data-testid="performer-events-empty">No {tab} events{tab === 'upcoming' ? ' yet — check back soon' : ''}.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {events.map(e => <EventCard key={e.event_id} e={e} past={tab === 'past'} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
